import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CalendarX, Users } from "lucide-react";

export default function AmenitiesAdminPage() {
  const { propertyId } = useProperty();
  const [amenities, setAmenities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", hours: "9am - 9pm", capacity: 10, requires_approval: false, image: "" });
  const [blackoutOpen, setBlackoutOpen] = useState(false);
  const [blackoutTarget, setBlackoutTarget] = useState(null);
  const [blackoutDates, setBlackoutDates] = useState("");

  const loadA = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/amenities?property_id=${propertyId}`);
      setAmenities(data.items || []);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/AmenitiesAdminPage.jsx: swallowed', err); }
  }, [propertyId]);
  const loadB = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/bookings?property_id=${propertyId}`);
      setBookings(data.items || []);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/AmenitiesAdminPage.jsx: swallowed', err); }
  }, [propertyId]);
  useEffect(() => { loadA(); loadB(); }, [loadA, loadB]);

  const startEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name, hours: a.hours, capacity: a.capacity,
      requires_approval: !!a.requires_approval, image: a.image || "",
    });
    setOpen(true);
  };
  const startNew = () => {
    setEditing(null);
    setForm({ name: "", hours: "9am - 9pm", capacity: 10, requires_approval: false, image: "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      if (editing) {
        await api.patch(`/admin/amenities/${editing.id}`, {
          ...form, capacity: Number(form.capacity) || 10,
        });
        toast.success("Updated");
      } else {
        await api.post(`/admin/amenities?property_id=${propertyId}`, {
          ...form, capacity: Number(form.capacity) || 10,
        });
        toast.success("Amenity added");
      }
      setOpen(false);
      loadA();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.name}? Existing bookings remain but no new ones can be made.`)) return;
    try {
      await api.delete(`/admin/amenities/${a.id}`);
      toast.success("Deleted");
      loadA();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const saveBlackout = async () => {
    const dates = blackoutDates.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await api.post(`/admin/amenities/${blackoutTarget.id}/blackout`, {
        dates, reason: null,
      });
      toast.success("Blackout saved");
      setBlackoutOpen(false);
      loadA();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const decideBooking = async (b, decision) => {
    try {
      await api.patch(`/admin/bookings/${b.id}/decide`, { decision, note: null });
      toast.success(decision === "approved" ? "Approved" : "Denied");
      loadB();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Facilities"
        title="Amenities"
        subtitle="Create bookable spaces, review pending reservations, and set blackout dates."
        actions={
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 rounded-full" onClick={startNew} data-testid="new-amenity-btn">
            <Plus className="h-4 w-4 mr-1" /> New amenity
          </Button>
        }
      />

      <Tabs defaultValue="amenities">
        <TabsList className="bg-white/[0.03] border border-white/10">
          <TabsTrigger value="amenities" data-testid="tab-amenities">Amenities ({amenities.length})</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="amenities" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {amenities.map((a) => (
              <Card key={a.id} className="overflow-hidden border-white/10 bg-card" data-testid={`amenity-${a.id}`}>
                {a.image && <div className="h-32 bg-zinc-900"><img src={a.image} alt={a.name} className="w-full h-full object-cover opacity-80" /></div>}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-lg font-semibold font-display">{a.name}</div>
                    {a.requires_approval && <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10">Approval</Badge>}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
                    <span>{a.hours}</span>
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {a.capacity}</span>
                  </div>
                  {a.blackouts?.length > 0 && (
                    <div className="text-xs text-rose-300 mt-2">Blackout: {a.blackouts.join(", ")}</div>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" className="border-white/10" onClick={() => startEdit(a)} data-testid={`edit-${a.id}`}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="border-white/10" onClick={() => { setBlackoutTarget(a); setBlackoutDates((a.blackouts || []).join(", ")); setBlackoutOpen(true); }} data-testid={`blackout-${a.id}`}>
                      <CalendarX className="h-3 w-3 mr-1" /> Blackout
                    </Button>
                    <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-300 ml-auto" onClick={() => remove(a)} data-testid={`delete-${a.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
            {amenities.length === 0 && (
              <Card className="col-span-full border-dashed border-white/10 p-10 text-center text-muted-foreground">No amenities yet.</Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="bookings" className="mt-4">
          <Card className="border-white/10 bg-card overflow-hidden">
            <div className="divide-y divide-white/5">
              {bookings.map((b) => (
                <div key={b.id} className="px-4 py-3 flex items-center gap-4" data-testid={`booking-${b.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{b.amenity_name || "Amenity"}</div>
                    <div className="text-[11px] text-zinc-500">{b.resident_name || b.user_name} · Unit {b.unit || "—"} · {b.date} {b.start_time}–{b.end_time}</div>
                  </div>
                  <Badge variant="outline" className="border-white/10 capitalize">{b.status}</Badge>
                  {b.status === "pending" && (
                    <div className="flex gap-1">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decideBooking(b, "approved")} data-testid={`booking-approve-${b.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-300" onClick={() => decideBooking(b, "denied")} data-testid={`booking-deny-${b.id}`}>Deny</Button>
                    </div>
                  )}
                </div>
              ))}
              {bookings.length === 0 && <div className="px-4 py-8 text-center text-muted-foreground">No bookings yet.</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.name}` : "New amenity"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="amenity-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Hours</Label><Input data-testid="amenity-hours" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} /></div>
              <div><Label>Capacity</Label><Input data-testid="amenity-capacity" type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
            </div>
            <div><Label>Image URL (optional)</Label><Input data-testid="amenity-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} /></div>
            <div className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.02] p-3">
              <div><div className="text-sm">Requires approval</div><div className="text-xs text-zinc-500">Bookings queue as pending until admin approves</div></div>
              <Switch data-testid="amenity-approval" checked={form.requires_approval} onCheckedChange={(v) => setForm({ ...form, requires_approval: v })} />
            </div>
            <Button data-testid="amenity-save" onClick={save} className="w-full bg-orange-500 hover:bg-orange-600">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={blackoutOpen} onOpenChange={setBlackoutOpen}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader><DialogTitle>Blackout dates · {blackoutTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Dates (comma-separated ISO, e.g. 2026-02-14, 2026-02-15)</Label>
              <Input data-testid="blackout-dates" value={blackoutDates} onChange={(e) => setBlackoutDates(e.target.value)} placeholder="2026-02-14, 2026-02-15" />
            </div>
            <Button data-testid="blackout-save" onClick={saveBlackout} className="w-full bg-orange-500 hover:bg-orange-600">Save blackout</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
